import React, { useState, useEffect } from 'react';
import { doctors } from '../data/mock';
import { GraduationCap, Briefcase, Building2, ArrowLeft, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const AllDoctors = () => {
  const navigate = useNavigate();
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');

  const specialties = ['All', 'Orthopedic Surgeon', 'Oncologist', 'Dental Specialist', 'Cosmetic Surgeon'];
  
  const filteredDoctors = selectedSpecialty === 'All' 
    ? doctors 
    : doctors.filter(doc => doc.specialty === selectedSpecialty);

  return (
    <div>
      {/* SEO Meta Tags with self-referencing canonical */}
      <SEOHead 
        title="Our Doctors | Top Surgeons in India - Curis Path"
        description="Meet our 18+ internationally trained specialists in Orthopedics, Oncology, Dental Care, and Cosmetic Surgery. World-class surgeons at India's top hospitals."
        canonical="/doctors"
      />
      
      <Header />
      
      <section className="section-padding" style={{ background: 'var(--bg-page)', marginTop: '80px' }}>
        <div className="container">
          <div className="mb-8">
            <button 
              onClick={() => navigate('/')}
              className="btn-secondary flex items-center gap-2 mb-6"
            >
              <ArrowLeft size={18} />
              Back to Home
            </button>
            
            <h1 className="heading-hero mb-4">All Expert Doctors</h1>
            <p className="body-large mb-6" style={{ maxWidth: '800px', color: 'var(--text-secondary)' }}>
              Complete directory of {doctors.length} internationally trained specialists in Orthopedics, Oncology, Dental Care, and Cosmetic Surgery
            </p>

            {/* Specialty Filter */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <div className="flex items-center gap-2 body-small" style={{ color: 'var(--text-primary)' }}>
                <Filter size={18} />
                <span className="font-semibold">Filter by Specialty:</span>
              </div>
              {specialties.map((specialty) => (
                <button
                  key={specialty}
                  onClick={() => setSelectedSpecialty(specialty)}
                  className="caption px-4 py-2 rounded-full transition-all"
                  style={{
                    background: selectedSpecialty === specialty ? 'var(--text-primary)' : 'rgba(255, 255, 255, 0.6)',
                    color: selectedSpecialty === specialty ? 'white' : 'var(--text-primary)',
                    border: '1px solid ' + (selectedSpecialty === specialty ? 'var(--text-primary)' : 'rgba(0, 0, 0, 0.1)'),
                    cursor: 'pointer',
                    fontWeight: selectedSpecialty === specialty ? '600' : '400'
                  }}
                >
                  {specialty}
                </button>
              ))}
            </div>

            <div className="inline-block px-4 py-2 rounded-lg mb-4" style={{ background: 'var(--accent-blue-200)' }}>
              <span className="body-small font-semibold">
                Showing {filteredDoctors.length} doctor{filteredDoctors.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDoctors.map((doctor, index) => {
              const colorClasses = ['accent-blue', 'accent-purple', 'accent-orange', 'accent-pink', 'accent-green', 'accent-grey'];
              const colorClass = colorClasses[index % colorClasses.length];
              
              return (
                <div 
                  key={doctor.id} 
                  className={`voice-card ${colorClass}`}
                >
                  <div className="mb-4 rounded-lg overflow-hidden" style={{ height: '240px' }}>
                    <img 
                      src={doctor.image} 
                      alt={doctor.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {doctor.featured && (
                    <div 
                      className="inline-block px-2 py-1 rounded-full mb-2"
                      style={{ 
                        background: 'var(--text-primary)',
                        color: 'white'
                      }}
                    >
                      <span className="caption font-semibold" style={{ fontSize: '0.65rem' }}>
                        FEATURED
                      </span>
                    </div>
                  )}

                  <h3 className="heading-3 mb-1">{doctor.name}</h3>
                  <p className="body-small font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                    {doctor.specialty}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2">
                      <GraduationCap size={18} style={{ color: 'var(--text-muted)', marginTop: '2px', flexShrink: 0 }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {doctor.qualification}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Briefcase size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {doctor.experience} Experience
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Building2 size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {doctor.hospital}
                      </span>
                    </div>

                    {doctor.specialization && (
                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <p className="caption font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                          Specialization:
                        </p>
                        <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                          {doctor.specialization}
                        </p>
                      </div>
                    )}
                  </div>

                  <button 
                    className="btn-secondary w-full"
                    onClick={() => {
                      navigate('/');
                      setTimeout(() => {
                        document.getElementById('enquiry')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                  >
                    Book Consultation
                  </button>
                </div>
              );
            })}
          </div>

          {filteredDoctors.length === 0 && (
            <div className="text-center py-12">
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                No doctors found for the selected specialty.
              </p>
            </div>
          )}

          <div className="mt-12 text-center">
            <div 
              className="voice-card accent-grey inline-block"
              style={{ padding: '2rem', maxWidth: '600px' }}
            >
              <h3 className="heading-2 mb-3">Ready to Consult Our Experts?</h3>
              <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                Get in touch with us to schedule a consultation with our world-class doctors
              </p>
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    document.getElementById('enquiry')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="btn-primary"
              >
                Submit Enquiry
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AllDoctors;
